/**
 * IXVerify App Component
 *
 * Integrated into SiteOperations.io - uses shared authentication.
 * Users log in once via the main app and can access IXVerify if they have access.
 * Note: IXVerifyAuthProvider is now in App.tsx to persist state across navigation.
 */

import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useIXVerifyAuth } from './contexts';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from './components/layout';
import api from '../services/api';
import {
  Dashboard,
  NewVerification,
  SimpleVerification,
  VerificationHistory,
  ShipmentVerification,
  SavedRecipients,
  UserManagement,
  AuditLogs,
  SuperAdmin,
  Settings,
  Notifications,
  StudyManagement,
} from './pages';

// Protected route wrapper for IXVerify - uses shared auth
function ProtectedRoute({ children, requireAdmin = false, requireSuperAdmin = false }: {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
}) {
  const { user, supabaseUser, loading } = useIXVerifyAuth();
  const { user: authUser, currentSite } = useAuth();
  const navigate = useNavigate();
  const [entitlementChecked, setEntitlementChecked] = useState(false);
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(true);

  // Check entitlement when component mounts or site changes
  useEffect(() => {
    const checkEntitlement = async () => {
      // Super admins always have access
      if (authUser?.role === 'super_admin') {
        setHasEntitlement(true);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      if (!currentSite) {
        // No site selected = no access to tools
        setHasEntitlement(false);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      try {
        const response = await api.get('/api/my-tools');
        const tools = response.data || [];
        const ixverifyTool = tools.find((t: any) => t.tool_id === 'ixverify');
        setHasEntitlement(ixverifyTool?.is_available === true);
        setEntitlementChecked(true);
      } catch (error) {
        console.error('Error checking IXVerify entitlement:', error);
        setHasEntitlement(false);
        setEntitlementChecked(true);
      } finally {
        setCheckingEntitlement(false);
      }
    };

    if (supabaseUser && !loading) {
      checkEntitlement();
    }
  }, [supabaseUser, authUser?.role, currentSite, loading]);

  if (loading || checkingEntitlement) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  // If no Supabase session, redirect to main login
  if (!supabaseUser) {
    return <Navigate to="/login" replace />;
  }

  // Check entitlement (super admins bypass this check)
  if (entitlementChecked && !hasEntitlement && authUser?.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-100">Access Denied</h1>
          <p className="text-gray-600 mb-6 dark:text-gray-400">
            You do not have access to IXVerify for your current site.
            Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  // Still loading user profile
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  if (requireSuperAdmin && user.role !== 'superadmin') {
    return <Navigate to="/ixverify/dashboard" replace />;
  }

  if (requireAdmin && !['admin', 'superadmin'].includes(user.role || '')) {
    return <Navigate to="/ixverify/dashboard" replace />;
  }

  return <>{children}</>;
}

// IXVerify Routes - no separate login, uses main app auth
function IXVerifyRoutes() {
  return (
    <Routes>
      {/* Default route goes to dashboard */}
      <Route path="/" element={<Navigate to="/ixverify/dashboard" replace />} />

      {/* Main dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Verification routes */}
      <Route
        path="/verify"
        element={
          <ProtectedRoute>
            <SimpleVerification />
          </ProtectedRoute>
        }
      />
      {/* Legacy verification route (keep for backwards compatibility) */}
      <Route
        path="/verify-legacy"
        element={
          <ProtectedRoute>
            <Layout>
              <NewVerification />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <Layout>
              <VerificationHistory />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* TODO: Re-enable when shipment reconciliation feature is ready */}
      {/* <Route
        path="/shipment"
        element={
          <ProtectedRoute>
            <Layout>
              <ShipmentVerification />
            </Layout>
          </ProtectedRoute>
        }
      /> */}

      {/* Recipients */}
      <Route
        path="/recipients"
        element={
          <ProtectedRoute>
            <Layout>
              <SavedRecipients />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* User settings & notifications */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Layout>
              <Notifications />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/users"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <UserManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/studies"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <StudyManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-logs"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <AuditLogs />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Super Admin routes */}
      <Route
        path="/super-admin"
        element={
          <ProtectedRoute requireSuperAdmin>
            <Layout>
              <SuperAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/ixverify/dashboard" replace />} />
    </Routes>
  );
}

// Main IXVerify App
// Note: Provider is now in App.tsx to persist state across navigation
// IXVerify is currently turned OFF platform-wide. The tool's UI is disabled here
// (default off — REACT_APP_IXVERIFY_ENABLED is unset) and its Claude Vision OCR
// edge functions (extract-ids / generate-and-send) are hard-disabled server-side.
// To re-enable the tool you must flip BOTH: set REACT_APP_IXVERIFY_ENABLED='true'
// for the frontend AND set IXVERIFY_ENABLED='true' on the edge functions (and
// restore verify_jwt as desired in supabase/config.toml).
const IXVERIFY_ENABLED = process.env.REACT_APP_IXVERIFY_ENABLED === 'true';

export function IXVerifyApp() {
  if (!IXVERIFY_ENABLED) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🚧</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-100">IXVerify is unavailable</h1>
          <p className="text-gray-600 mb-6 dark:text-gray-400">
            This tool has been turned off. Please contact your administrator if you
            believe you need access.
          </p>
          <a
            href="/"
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
          >
            Return to Tool Hub
          </a>
        </div>
      </div>
    );
  }
  return <IXVerifyRoutes />;
}

export default IXVerifyApp;
